// Renderer for the "Dynamic At a glance (M&E)" layout.
//
// A separate module from atAGlanceParser rather than a branch inside it: that
// function is 360 lines serving every existing dynamic-at-a-glance slide, and
// the M&E variant restructures both columns. Keeping them apart means the
// original layout cannot regress.
//
// The data readers are imported from the hotel-profile screen rather than
// reimplemented. The block stores `apiData` as the verbatim /hotel-detail
// response, which is exactly what those readers take, so the slide and the
// profile screen agree by construction - including on the awkward parts
// (repeatable properties that arrive bare or wrapped in {values: []}, "/" as a
// "not offered" placeholder, capacities published with no layout taxonomy).
import { buildAagExpandSection } from "./aagExpandSection";
import { HotelLocation } from "../../../../../editorjs/icons/icons";
import {
  capacityFor,
  decodeHTML,
  readMeetingFeatures,
  readHotelGalleryCategories,
  readMeetingGallery,
  readMeetingRooms,
  readOutlets,
} from "../../../../rhgHotel/hotelData";

/* ── image sizing ─────────────────────────────────────────────────────────── */

// The CDN encodes the rendition in the filename, not in a query string:
// …-f67041458_TH128.jpg is 128x128, _S 75x50, _M 390x260, _L 600x400,
// _XL 1023x682, _3XL 2879x1920. `urlThumbnail` from the API points at TH128,
// so anything rendered bigger than a favicon has to ask for a larger variant
// by name. Unknown filename shapes are returned untouched.
const CDN_VARIANT = /_(?:TH\d+|XS|S|M|L|XL|\d?XL)(\.[a-z]+)$/i;

const sized = (url, variant) =>
  typeof url === "string" && CDN_VARIANT.test(url)
    ? url.replace(CDN_VARIANT, `_${variant}$1`)
    : url || "";

// The multimedia arrays mix stills and video renditions; a video URL in an
// <img> is just a broken tile.
const isStill = (url) =>
  typeof url === "string" && !!url && !url.includes("/video/");

/* ── gallery viewer ───────────────────────────────────────────────────────── */

// The slide HTML is a string handed to reveal, so the viewer is driven by
// global handlers and a data attribute rather than by component state - same
// approach the rest of this layout uses.
const viewerOf = (el) =>
  el.closest(".at-a-glance-content-container")?.querySelector(".aag-me-viewer");

function viewerShow(viewer, index) {
  let slides = [];
  try {
    slides = JSON.parse(viewer.getAttribute("data-slides") || "[]");
  } catch (e) {
    slides = [];
  }
  if (!slides.length) return;

  const i = ((index % slides.length) + slides.length) % slides.length;
  viewer.setAttribute("data-index", String(i));

  const img = viewer.querySelector(".aag-me-viewer-img");
  img.src = slides[i].full;
  img.alt = slides[i].caption || "";
  viewer.querySelector(".aag-me-viewer-caption").textContent =
    slides[i].caption || "";
  const slide = slides[i];
  viewer.querySelector(".aag-me-viewer-count").textContent = slide.gt
    ? `${slide.gi} / ${slide.gt}`
    : `${i + 1} / ${slides.length}`;
  const groupEl = viewer.querySelector(".aag-me-viewer-group-name");
  if (groupEl) groupEl.textContent = slide.group || "";

  const active = viewer.querySelector(`.aag-me-viewer-thumb[data-index="${i}"]`);
  viewer.querySelectorAll(".aag-me-viewer-thumb").forEach((t) => {
    t.classList.toggle("is-active", t === active);
  });

  // Keep the active thumbnail in view, opening its group if it is collapsed.
  if (active) {
    const group = active.closest(".aag-me-viewer-group");
    if (group && !group.classList.contains("is-open")) {
      group.classList.add("is-open");
    }
    active.scrollIntoView({ block: "nearest" });
  }

  // The sidebar is lazy: only load the thumbnails of groups the viewer has
  // actually opened, otherwise a 229-image hotel fetches 229 files on open.
  viewer
    .querySelectorAll(".aag-me-viewer-group.is-open img[data-src]")
    .forEach((el) => {
      el.src = el.getAttribute("data-src");
      el.removeAttribute("data-src");
    });
}

window.aagMeViewerOpen = function (btn, index) {
  const viewer = viewerOf(btn);
  if (!viewer) return;
  viewer.classList.add("is-open");
  viewerShow(viewer, index);
};

window.aagMeViewerClose = function (btn) {
  const viewer = viewerOf(btn);
  if (!viewer) return;
  viewer.classList.remove("is-open");
  viewer.querySelector(".aag-me-viewer-img").src = "";
};

window.aagMeViewerGo = function (btn, index) {
  const viewer = viewerOf(btn);
  if (viewer) viewerShow(viewer, index);
};

window.aagMeViewerStep = function (btn, delta) {
  const viewer = viewerOf(btn);
  if (!viewer) return;
  viewerShow(viewer, Number(viewer.getAttribute("data-index") || 0) + delta);
};

window.aagMeViewerGroup = function (btn) {
  const group = btn.closest(".aag-me-viewer-group");
  group.classList.toggle("is-open");
  if (group.classList.contains("is-open")) {
    group.querySelectorAll("img[data-src]").forEach((el) => {
      el.src = el.getAttribute("data-src");
      el.removeAttribute("data-src");
    });
  }
};

/* ── panel switching ──────────────────────────────────────────────────────── */

// The left column holds every panel at once and the container attribute says
// which is visible, so switching is one attribute write and CSS does the rest.
// Matches how the original layout toggles its map.
window.aagMePanel = function (btn, panel) {
  const container = btn.closest(".at-a-glance-content-container");
  if (!container) return;

  const current = container.getAttribute("data-panel") || "hero";
  // Clicking the active button returns to the hero, so every button toggles.
  const next = current === panel ? "hero" : panel;
  container.setAttribute("data-panel", next);

  container.querySelectorAll(".aag-me-nav-btn").forEach((el) => {
    el.classList.toggle("is-active", el.getAttribute("data-panel") === next);
  });

  // Iframes are kept unloaded until first use: a slide deck can hold 80 of
  // these and booting a Google map in each on render is what makes a deck
  // crawl. data-src is promoted to src once, then left alone.
  if (next !== "hero") {
    container
      .querySelectorAll(`[data-panel-body="${next}"] iframe[data-src]`)
      .forEach((frame) => {
        frame.src = frame.getAttribute("data-src");
        frame.removeAttribute("data-src");
      });
  }

  // The key-features list hides its "scroll for more" chevron until the list
  // actually overflows. That used to be refreshed by the centre expand button;
  // with the panel opened from the nav instead, this is the trigger. It has to
  // wait for the panel transition, or the element is still zero-height.
  if (next === "keyfeatures") {
    const scrollEl = container.querySelector(
      '[data-panel-body="keyfeatures"] .expand-scroll',
    );
    if (scrollEl && typeof window.aagUpdateExpandChevron === "function") {
      setTimeout(() => window.aagUpdateExpandChevron(scrollEl), 450);
    }
  }
};

// Widens the left column to the full slide, hiding the right column. The
// chevron is the only way back, so it stays put and flips to point left.
window.aagMeExpandColumns = function (btn) {
  const container = btn.closest(".at-a-glance-content-container");
  if (!container) return;
  const on = container.classList.toggle("cols-expanded");
  btn.setAttribute("aria-expanded", on ? "true" : "false");
  btn.setAttribute(
    "aria-label",
    on ? "Collapse to two columns" : "Expand to full width",
  );

  // The capacity table sizes its sticky columns against the panel width, and
  // the map iframe re-centres on resize, so both need a nudge once the
  // column transition has finished.
  setTimeout(() => window.dispatchEvent(new Event("resize")), 650);
};

// Toggles the distances card over the location map.
window.aagMeMapInfo = function (btn) {
  const wrap = btn.closest(".aag-me-map-wrap");
  if (!wrap) return;
  wrap.classList.toggle("show-info");
  btn.classList.toggle("is-on", wrap.classList.contains("show-info"));
};

/* ── helpers ──────────────────────────────────────────────────────────────── */

// Everything interpolated into the template is escaped. The payload is
// third-party content and some of it (outlet names, room titles) is free text.
const esc = (value) =>
  String(value == null ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const text = (value) => esc(decodeHTML(String(value == null ? "" : value)));

const list = (value) => {
  if (value == null) return [];
  if (Array.isArray(value)) return value.filter((v) => v != null);
  if (typeof value === "object" && "values" in value) {
    const inner = value.values;
    if (Array.isArray(inner)) return inner.filter((v) => v != null);
    return inner == null ? [] : [inner];
  }
  return [value];
};

const tagHtml = (items) =>
  items.length
    ? `<div class="aag-me-tags">${items
        .map((t) => `<span class="aag-me-tag">${text(t)}</span>`)
        .join("")}</div>`
    : "";

const ICONS = {
  map: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11Z"/><circle cx="12" cy="10" r="2.6"/></svg>',
  capacity:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><circle cx="7" cy="9" r="2.4"/><circle cx="12" cy="7.5" r="2.4"/><circle cx="17" cy="9" r="2.4"/><path d="M3 17c0-2.2 1.8-3.6 4-3.6s4 1.4 4 3.6"/><path d="M13 17c0-2.2 1.8-3.6 4-3.6s4 1.4 4 3.6"/></svg>',
  features:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><rect x="5" y="3.5" width="14" height="17" rx="2"/><path d="M9 3.5h6v3H9z"/><path d="M8.5 11h7M8.5 15h4"/></svg>',
  gallery:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><rect x="3.5" y="5" width="17" height="14" rx="2"/><circle cx="9" cy="10" r="1.6"/><path d="m4.5 17 4.5-4.5 3.5 3.5 3-2.5 4 3.5"/></svg>',
  dining:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><circle cx="12" cy="12" r="8.5"/><path d="M12 3.5v17M8 3.9c2 2.6 2 13.6 0 16.2"/></svg>',
  keyfeatures:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><circle cx="12" cy="12" r="3.2"/><path d="M19.4 14a1.5 1.5 0 0 0 .3 1.7l.1.1a1.8 1.8 0 1 1-2.6 2.6l-.1-.1a1.5 1.5 0 0 0-1.7-.3 1.5 1.5 0 0 0-.9 1.4v.2a1.8 1.8 0 1 1-3.6 0v-.1a1.5 1.5 0 0 0-1-1.4 1.5 1.5 0 0 0-1.7.3l-.1.1a1.8 1.8 0 1 1-2.6-2.6l.1-.1a1.5 1.5 0 0 0 .3-1.7 1.5 1.5 0 0 0-1.4-.9h-.2a1.8 1.8 0 1 1 0-3.6h.1a1.5 1.5 0 0 0 1.4-1 1.5 1.5 0 0 0-.3-1.7l-.1-.1a1.8 1.8 0 1 1 2.6-2.6l.1.1a1.5 1.5 0 0 0 1.7.3H10a1.5 1.5 0 0 0 .9-1.4v-.2a1.8 1.8 0 1 1 3.6 0v.1a1.5 1.5 0 0 0 .9 1.4 1.5 1.5 0 0 0 1.7-.3l.1-.1a1.8 1.8 0 1 1 2.6 2.6l-.1.1a1.5 1.5 0 0 0-.3 1.7V10a1.5 1.5 0 0 0 1.4.9h.2a1.8 1.8 0 1 1 0 3.6h-.1a1.5 1.5 0 0 0-1.4.9Z"/></svg>',
  website:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><circle cx="12" cy="12" r="8.5"/><path d="M3.5 12h17"/><path d="M12 3.5c2.4 2.6 2.4 14.4 0 17M12 3.5c-2.4 2.6-2.4 14.4 0 17"/></svg>',
};

// The seating-layout icons already ship with the theme, on the data-table
// layout (theatre, classroom, boardroom, u-shape, cabaret, banquet, drinks
// reception...). Reading them from there beats inlining another dozen SVGs,
// and they stay in step with the brand if the theme updates.
const normaliseIconName = (value) =>
  String(value == null ? "" : value)
    .toLowerCase()
    .replace(/[^a-z]/g, "");

// Capacity columns are named by the feed; these are the ones whose label does
// not match an icon name directly.
const LAYOUT_ICON_ALIASES = {
  rounds: "banquet",
  round: "banquet",
  lunch: "banquet",
  dinner: "banquet",
  dinnerdance: "dinner dance",
  reception: "drinks reception",
  hollowsquare: "hollow square",
};

const seatingIcon = (themeJson, label) => {
  const icons =
    (themeJson?.layouts || []).find((l) => l?.id === "data-table")?.icons || [];
  if (!icons.length) return "";
  const key = normaliseIconName(label);
  const target = normaliseIconName(LAYOUT_ICON_ALIASES[key] || label);
  // The theme ships each diagram twice: the plain one is filled #FFFFFF for
  // the dark data-table layout, the "… dark" one is #333333. This table has a
  // light header, so the dark variant is the one that shows; the plain one is
  // the fallback for any layout that only has the one.
  const byName = (name) => icons.find((i) => normaliseIconName(i?.name) === name);
  const hit = byName(target + "dark") || byName(target);
  return hit?.icon || "";
};

const navBtn = (panel, label, icon) =>
  `<button type="button" class="aag-me-nav-btn" data-panel="${esc(panel)}"
     onclick="event.stopPropagation(); aagMePanel(this, '${esc(panel)}')">
     <span class="aag-me-nav-icon">${icon}</span>
     <span class="aag-me-nav-label">${label}</span>
   </button>`;

const panel = (name, body) =>
  `<div class="aag-me-panel" data-panel-body="${esc(name)}">${body}</div>`;

const emptyPanel = (message) =>
  `<div class="aag-me-empty">${text(message)}</div>`;

/* ── panels ───────────────────────────────────────────────────────────────── */

// Mirrors the hotel-profile capacity matrix: one column per layout the hotel
// actually publishes, "-" where a room does not offer it.
function capacityPanel(meeting, themeJson) {
  if (!meeting.rooms.length) {
    return emptyPanel("No meeting rooms published for this hotel.");
  }

  const cols = meeting.layouts;
  // Most hotels publish no metric at all, so the unit falls back to m² exactly
  // as the hotel-profile capacity table does - the two must not disagree on
  // the same hotel.
  const sizeUnit = meeting.metric || "m\u00B2";
  // Each layout column gets the theme's seating diagram above its label; a
  // layout with no matching icon just shows the label.
  const head =
    `<tr><th class="aag-me-th-name">Meeting room</th><th>Size ${text(sizeUnit)}</th>` +
    cols
      .map((c) => {
        const icon = seatingIcon(themeJson, c.label);
        // Icon only, as on the data-table layout. The label is kept in the
        // title and as screen-reader text rather than dropped, so the column
        // is still identifiable without sighted access to the diagram.
        return icon
          ? `<th title="${esc(c.label)}"><span class="aag-me-seat-icon">${icon}</span>` +
              `<span class="aag-me-seat-label is-sr">${text(c.label)}</span></th>`
          : `<th><span class="aag-me-seat-label">${text(c.label)}</span></th>`;
      })
      .join("") +
    `</tr>`;

  const rows = meeting.rooms
    .map((room) => {
      // Some rooms publish capacities with the layout taxonomy stripped, so the
      // numbers arrive with nothing saying which setup each is for. Showing
      // them bare under a labelled column would be a guess; they get their own
      // full-width note instead, as on the profile screen.
      if (room.shapesUnlabelled) {
        // The note leads and the figures follow as prose. Putting the bare
        // numbers first made them sit directly under Theatre, Classroom and
        // Boardroom, so the row read as though each figure belonged to the
        // column above it - on this hotel that would have claimed Atelier
        // seats 100 theatre-style, which nothing in the feed supports.
        return `<tr class="aag-me-row-unlabelled">
          <td class="aag-me-td-name">${text(room.name)}</td>
          <td>${text(room.size)}</td>
          <td colspan="${cols.length || 1}">
            <span class="aag-me-note">Capacities published without layout
              types:</span>
            <span class="aag-me-bare">${room.shapes
              .map((s) => text(s.value))
              .join(", ")}</span>
          </td>
        </tr>`;
      }
      const cells = cols
        .map((c) => {
          const v = capacityFor(room, c.key);
          return `<td>${v == null ? "&ndash;" : text(v.toLocaleString())}</td>`;
        })
        .join("");
      return `<tr>
        <td class="aag-me-td-name">${text(room.name)}</td>
        <td>${text(room.size)}</td>${cells}
      </tr>`;
    })
    .join("");

  return `<div class="aag-me-panel-head"><h3>Capacity chart</h3></div>
    <div class="aag-me-table-scroll">
      <table class="aag-me-table"><thead>${head}</thead><tbody>${rows}</tbody></table>
    </div>`;
}

function featuresPanel(features, meeting) {
  if (
    !features.features.length &&
    !features.hotelDescription &&
    !meeting.rooms.length
  ) {
    return emptyPanel("No meeting and event features published.");
  }

  const counts = features.counts
    .map(
      (c) => `<div class="aag-me-count">
        <span class="aag-me-count-value">${text(c.text)}</span>
        <span class="aag-me-count-label">${text(c.label)}</span>
      </div>`,
    )
    .join("");

  // Proper cards rather than text blocks: each room leads with its own photo
  // and the figures an organiser scans for, then the description.
  const rooms = meeting.rooms
    .filter((r) => r.description || r.thumbnail)
    .map((r) => {
      const peak = r.shapes
        .map((sh) => Number(String(sh.value).replace(/[^\d]/g, "")))
        .filter((n) => Number.isFinite(n) && n > 0);
      const facts = [
        r.size ? `${text(r.size)} ${text(meeting.metric || "m\u00B2")}` : "",
        peak.length ? `up to ${Math.max(...peak)}` : "",
      ].filter(Boolean);

      return `<article class="aag-me-room-card">
        ${
          r.thumbnail
            ? `<div class="aag-me-room-card__media"><img data-src="${esc(
                sized(r.thumbnail, "L"),
              )}" alt="${esc(r.altText || r.name)}" loading="lazy" /></div>`
            : ""
        }
        <div class="aag-me-room-card__body">
          <h4>${text(r.name)}</h4>
          ${
            facts.length
              ? `<p class="aag-me-room-card__facts">${facts.join(" &middot; ")}</p>`
              : ""
          }
          ${r.description ? `<p>${text(r.description)}</p>` : ""}
        </div>
      </article>`;
    })
    .join("");

  // The heading, lede, chips, capacity and RFP line are one centred intro
  // block, wrapped so the centring and the narrower measure stop at the room
  // grid - the cards below stay on the panel's own full width.
  //
  // features.heading (the "Key features" kicker that sat opposite the title)
  // is deliberately not rendered: it labelled the chips, which read as a set
  // without it, and it was the one thing breaking the centred column.
  return `<div class="aag-me-features-intro">
      <div class="aag-me-panel-head"><h3>Meeting &amp; event features</h3></div>
      ${
        features.hotelDescription
          ? `<p class="aag-me-lede">${text(features.hotelDescription)}</p>`
          : ""
      }
      ${tagHtml(features.features.map((f) => f.label))}
      ${counts ? `<div class="aag-me-counts">${counts}</div>` : ""}
      ${
        features.rfpEmail
          ? `<p class="aag-me-rfp">RFP enquiries:
               <a href="mailto:${esc(features.rfpEmail)}">${text(
                 features.rfpEmail,
               )}</a></p>`
          : ""
      }
    </div>
    ${rooms ? `<div class="aag-me-room-grid">${rooms}</div>` : ""}`;
}

// Sidebar groups for the viewer: the M&E set first (that is what the slide is
// about and what the nav button promises), then every other published hotel
// category. Images already shown in the M&E group are dropped from the later
// ones so the running counter matches the number of distinct photos.
function galleryGroups(api, gallery) {
  const groups = [];
  const seen = new Set();

  const take = (images) =>
    images
      .filter((img) => isStill(img.url) && !seen.has(img.url))
      .map((img) => {
        seen.add(img.url);
        return img;
      });

  const me = take(gallery.images);
  if (me.length) groups.push({ label: "Meetings & events", images: me });

  // Category descriptions arrive HTML-encoded ("Bar &amp; Lounge"). Decoding
  // here keeps every consumer working on plain text: the sidebar heading
  // escapes it again for markup, while the viewer's group name is written
  // with textContent and would otherwise print the entity verbatim.
  readHotelGalleryCategories(api).forEach((cat) => {
    const images = take(cat.images);
    if (images.length) {
      groups.push({ label: decodeHTML(cat.label || "") || "Gallery", images });
    }
  });

  return groups;
}

function galleryPanel(gallery) {
  if (!gallery.images.filter((i) => isStill(i.url)).length) {
    return emptyPanel("No meeting and event images published.");
  }
  const images = gallery.images.filter((i) => isStill(i.url));
  const tiles = images
    .slice(0, 24)
    .map(
      (img, i) => `<button type="button" class="aag-me-shot"
        onclick="event.stopPropagation(); aagMeViewerOpen(this, ${i});">
        <img data-src="${esc(sized(img.url, "L"))}"
             alt="${esc(img.alt || "Meeting room")}" loading="lazy" />
      </button>`,
    )
    .join("");
  return `<div class="aag-me-panel-head"><h3>M&amp;E gallery</h3></div>
    <div class="aag-me-shots">${tiles}</div>`;
}

// Full-bleed viewer per the spec: stage on the left, collapsible category list
// on the right, caption bottom-left, counter and arrows bottom-right.
function galleryViewer(groups, hotelName) {
  if (!groups.length) return "";

  let n = 0;
  const flat = [];
  const sections = groups
    .map((group, gi) => {
      const thumbs = group.images
        .map((img) => {
          const index = n++;
          flat.push({ ...img, group: group.label });
          return `<button type="button" class="aag-me-viewer-thumb"
            data-index="${index}"
            onclick="event.stopPropagation(); aagMeViewerGo(this, ${index});">
            <img data-src="${esc(sized(img.url, "M"))}" alt="${esc(
              img.alt || group.label,
            )}" loading="lazy" />
          </button>`;
        })
        .join("");
      // Only the first group starts open, as in the spec - with eight or more
      // categories an all-open sidebar is one long scroll.
      // Deliberately a <div>: reveal treats any nested <section> as a vertical
      // slide, so a <section> here got absorbed into the deck - it turned the
      // slide into a stack, added phantom slides to the count and laid
      // generated .slide-background layers over the cover's View Details
      // button.
      return `<div class="aag-me-viewer-group${gi === 0 ? " is-open" : ""}">
        <button type="button" class="aag-me-viewer-group-head"
          onclick="event.stopPropagation(); aagMeViewerGroup(this);">
          <span>${text(group.label)} (${group.images.length})</span>
          <svg xmlns="http://www.w3.org/2000/svg" height="18" width="18"
               viewBox="0 -960 960 960" fill="currentcolor"><path d="M480-345 240-585l56-56 184 184 184-184 56 56-240 240Z"/></svg>
        </button>
        <div class="aag-me-viewer-thumbs">${thumbs}</div>
      </div>`;
    })
    .join("");

  // Alt text is usually already "<hotel> - <subject>", so the hotel name is
  // only prefixed when the caption does not carry it.
  const caption = (img) => {
    const subject = img.alt || img.caption || "";
    if (!hotelName) return subject;
    if (!subject) return hotelName;
    return subject.toLowerCase().includes(hotelName.toLowerCase())
      ? subject
      : `${hotelName} - ${subject}`;
  };

  // Each slide carries its position within its own group, not just within the
  // whole set. The grid shows "21 images" for Meetings & events, so a viewer
  // counter reading "4 / 34" across every category read as a different
  // gallery; now it reads "4 / 21 Meetings & events" and the two agree.
  const groupTotals = {};
  groups.forEach((g) => {
    groupTotals[g.label] = g.images.length;
  });
  const groupSeen = {};

  const slides = flat
    .map((img) => {
      groupSeen[img.group] = (groupSeen[img.group] || 0) + 1;
      return `{"full":"${esc(sized(img.url, "XL"))}","caption":"${esc(
        caption(img),
      )}","group":"${esc(img.group)}","gi":${groupSeen[img.group]},"gt":${
        groupTotals[img.group]
      }}`;
    })
    .join(",");

  return `<div class="aag-me-viewer" data-index="0"
       data-slides='[${slides}]' onclick="event.stopPropagation();">
    <button type="button" class="aag-me-viewer-close"
      onclick="event.stopPropagation(); aagMeViewerClose(this);" aria-label="Close">
      <svg xmlns="http://www.w3.org/2000/svg" height="26" width="26" viewBox="0 -960 960 960" fill="currentcolor"><path d="M256-200.62 200.62-256l224-224-224-224L256-759.38l224 224 224-224L759.38-704l-224 224 224 224L704-200.62l-224-224-224 224Z"/></svg>
    </button>
    <div class="aag-me-viewer-stage">
      <img class="aag-me-viewer-img" src="" alt="" />
      <div class="aag-me-viewer-bar">
        <p class="aag-me-viewer-caption"></p>
        <div class="aag-me-viewer-pager">
          <button type="button" onclick="event.stopPropagation(); aagMeViewerStep(this, -1);" aria-label="Previous">
            <svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 -960 960 960" fill="currentcolor"><path d="M560-240 320-480l240-240 56 56-184 184 184 184-56 56Z"/></svg>
          </button>
          <span class="aag-me-viewer-group-name"></span>
          <span class="aag-me-viewer-count"></span>
          <button type="button" onclick="event.stopPropagation(); aagMeViewerStep(this, 1);" aria-label="Next">
            <svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 -960 960 960" fill="currentcolor"><path d="M504-480 320-664l56-56 240 240-240 240-56-56 184-184Z"/></svg>
          </button>
        </div>
      </div>
    </div>
    <aside class="aag-me-viewer-side">${sections}</aside>
  </div>`;
}

function diningPanel(outlets) {
  if (!outlets.length) {
    return emptyPanel("No food and beverage outlets published.");
  }
  const cards = outlets
    .map((o) => {
      const hours = o.hours
        .slice(0, 4)
        .map(
          (h) => `<div class="aag-me-hours-row">
            <span>${text(h.type || "Opening")}</span>
            <span>${text(
              [h.from, h.to].filter(Boolean).join(" - ") || "-",
            )}</span>
          </div>`,
        )
        .join("");
      // Card per outlet: photo on top, then name, cuisine, description and
      // hours - so several outlets read as a set rather than a list of rows.
      return `<article class="aag-me-outlet">
        ${
          o.hero
            ? `<div class="aag-me-outlet-media"><img data-src="${esc(
                sized(o.hero, "L"),
              )}" alt="${esc(o.name)}" loading="lazy" /></div>`
            : `<div class="aag-me-outlet-media aag-me-outlet-media--empty"></div>`
        }
        <div class="aag-me-outlet-body">
          <h4>${text(o.name)}</h4>
          ${o.cuisine.length ? tagHtml(o.cuisine) : ""}
          ${o.description ? `<p>${text(o.description)}</p>` : ""}
          ${o.services.length ? tagHtml(o.services.slice(0, 6)) : ""}
          ${hours ? `<div class="aag-me-hours">${hours}</div>` : ""}
        </div>
      </article>`;
    })
    .join("");
  return `<div class="aag-me-panel-head"><h3>Food &amp; beverage</h3></div>
    <div class="aag-me-outlets">${cards}</div>`;
}

/* ── main ─────────────────────────────────────────────────────────────────── */

export function atAGlanceMEParser(block, theme_json) {
  const data = block?.data;
  if (!data) return "";

  const api = data.apiData || {};
  const hotel = api?.hotel || api?.hotelInfo?.collection?.[0] || null;
  const hm = hotel?.metadata || {};
  const hc = hotel?.content || {};
  const apiKey = theme_json?.googleApiKey;

  const meeting = readMeetingRooms(api);
  const features = readMeetingFeatures(api);
  const outlets = readOutlets(api);
  const gallery = readMeetingGallery(api, meeting.rooms);
  const galleryGroupList = galleryGroups(api, gallery);

  const roomContent =
    api?.roomInfo?.collection?.[0]?.content || hc?.roomInfo?.content || {};

  const hotelName = decodeHTML(data.hotelName || "") ||
    decodeHTML(hm?.tmsHotelCode?.description || "");
  const title = decodeHTML(data.title || hc?.text?.content?.title || "");
  const description = decodeHTML(
    data.description || hc?.text?.content?.shortDescription || "",
  );
  const city = decodeHTML(hm?.city?.description || "");
  const country = decodeHTML(hm?.country?.description || "");
  const hotelUrl = hotel?.linkUrl || "";
  // Published either bare or wrapped in {values: []}, like the rest of the
  // repeatable metadata - both shapes are read, as the base layout does.
  const hotelLocation = decodeHTML(
    hm?.location?.values?.[0]?.description || hm?.location?.description || "",
  );

  const overrides = data.imageOverrides || {};
  const picked = api?.images || api?.assets?.picked || {};
  const slotUrl = (slot) =>
    overrides[slot] || picked?.[slot]?.links?.cdnLinks?.[0] || "";
  const heroImage = overrides.hero || hc?.image?.binaryContent?.url || "";
  const heroOpacity = data.heroOpacity != null ? data.heroOpacity : 0.73;

  const brandLogos = theme_json?.brandLogos || {};
  const brandCode = hm?.brand?.key || "";
  const brandLogoSvg = brandCode && brandLogos[brandCode] ? brandLogos[brandCode] : "";

  // Distances are author-entered. The content-reader API has no distance
  // fields: hotelOTA's howToArrive is prose ("a 5.4-kilometer trip or via ...
  // a 6.8-kilometer trip"), carries several figures per entry for different
  // legs of the journey, has no motorway category, and exists for 2 of 865
  // hotels. Deriving a number from it would print a confident wrong answer.
  const distances = data.distances || {};
  const distanceRows = [
    ["Distance to airport", distances.airport],
    ["Distance to train station", distances.train],
    ["Distance to motorway", distances.motorway],
  ].filter(([, v]) => v != null && String(v).trim() !== "");

  // The services list is the only place the breakfast offer is published.
  const services = list(hc?.service)
    .map((s) => decodeHTML(((s && s.service) || s)?.description || ""))
    .filter(Boolean);
  const hasBreakfast = services.some((s) => /breakfast/i.test(s));

  const mapLat = hc?.coordinates?.latitude;
  const mapLng = hc?.coordinates?.longitude;
  const hasMap =
    !!apiKey &&
    mapLat != null &&
    mapLng != null &&
    !Number.isNaN(Number(mapLat)) &&
    !Number.isNaN(Number(mapLng));

  const stats = [
    [roomContent?.numOfBedrooms, "Guest<br/>rooms"],
    [roomContent?.numOfMeetingRooms, "Meeting<br/>rooms"],
    [roomContent?.maxCapacity, "Max<br/>cap"],
  ]
    .filter(([v]) => v != null && String(v).trim() !== "")
    .map(
      ([v, label]) => `<div class="hotel-capacity-group aag-me-stat">
        <span class="hotel-capacity-value aag-me-stat-value">${text(v)}</span>
        <span class="hotel-capacity-label aag-me-stat-label">${label}</span>
      </div>`,
    )
    .join("");

  const breakfastStat = hasBreakfast
    ? `<div class="hotel-capacity-group aag-me-stat">
         <span class="hotel-capacity-value aag-me-stat-value aag-me-tick">&#10004;</span>
         <span class="hotel-capacity-label aag-me-stat-label">Breakfast<br/>buffet</span>
       </div>`
    : "";

  const images = ["guestRoom", "meetingRoom"]
    .map((slot) => {
      const url = slotUrl(slot);
      return url
        ? `<div class="aag-me-img"><img data-src="${esc(sized(url, "L"))}"
             data-full="${esc(sized(url, "3XL"))}" alt="${esc(
            slot,
          )}" onclick="event.stopPropagation(); aagOpenFullscreen(this);" /></div>`
        : `<div class="aag-me-img aag-me-img-empty"></div>`;
    })
    .join("");

  const nav = [
    hasMap || distanceRows.length
      ? navBtn("map", "Location<br/>map", ICONS.map)
      : "",
    navBtn("capacity", "Capacity<br/>chart", ICONS.capacity),
    navBtn("features", "M&amp;E<br/>features", ICONS.features),
    navBtn("gallery", "M&amp;E<br/>gallery", ICONS.gallery),
    navBtn("dining", "Food &amp;<br/>beverage", ICONS.dining),
    navBtn("keyfeatures", "Key<br/>features", ICONS.keyfeatures),
  ]
    .filter(Boolean)
    .join("");

  // The same INDUSTRY SOLUTIONS / KEY FEATURES panel at-a-glance shows behind
  // its chevron, built by the shared module so the two layouts cannot drift.
  // Here it is a nav panel rather than a slide-level overlay, so the centre
  // chevron that used to reveal it is gone.
  const expandSection = buildAagExpandSection(hc, brandLogoSvg);

  // Distances sit over the map rather than in the right column: they describe
  // the location, so they belong with it, and the right column was carrying
  // four separate blocks before the nav.
  const distanceOverlay = distanceRows.length
    ? `<button type="button" class="aag-me-map-toggle"
         onclick="event.stopPropagation(); aagMeMapInfo(this);">
         <span class="aag-me-map-toggle-icon">${ICONS.map}</span>
         <span class="aag-me-map-toggle-label">Distances</span>
       </button>
       <div class="aag-me-map-overlay">
         <dl class="aag-me-distances">${distanceRows
           .map(
             ([label, value]) =>
               `<div class="aag-me-distance"><dt>${text(
                 label,
               )}</dt><dd>${text(value)}</dd></div>`,
           )
           .join("")}</dl>
       </div>`
    : "";

  const panels = [
    panel(
      "map",
      `<div class="aag-me-map-wrap">${
        hasMap
          ? `<iframe data-src="/map.html?entity=true&lat=${esc(mapLat)}&lng=${esc(
              mapLng,
            )}&zoom=16&key=${esc(apiKey)}" frameborder="0" title="Location map"></iframe>`
          : emptyPanel("No coordinates published for this hotel.")
      }${distanceOverlay}</div>`,
    ),
    panel("capacity", capacityPanel(meeting, theme_json)),
    panel("features", featuresPanel(features, meeting)),
    panel("gallery", galleryPanel(gallery)),
    panel("dining", diningPanel(outlets)),
    panel("keyfeatures", expandSection),
  ].join("");

  const closeBtnSvg =
    '<svg xmlns="http://www.w3.org/2000/svg" height="28" viewBox="0 -960 960 960" width="28" fill="currentcolor"><path d="M256-200.62 200.62-256l224-224-224-224L256-759.38l224 224 224-224L759.38-704l-224 224 224 224L704-200.62l-224-224-224 224Z"/></svg>';

  return `<div class="at-a-glance-content-container aag-me initial" data-panel="hero">
    <div class="at-a-glance-left">
      ${
        heroImage
          ? `<img class="at-a-glance-hero" data-src="${esc(
              sized(heroImage, "XL"),
            )}" data-full="${esc(sized(heroImage, "3XL"))}" alt="${esc(
              hotelName,
            )}" style="opacity: ${esc(
              heroOpacity,
            )};" onclick="aagOpenFullscreen(this);" />`
          : ""
      }
      <div class="content_block initial-overlay">
        <h2 class="initial-overlay-title">${text(hotelName)}</h2>
        <button class="view-details-btn" onclick="initialTransition(this)">
          <span class="view-details-btn-text">View Details &#8594;</span>
        </button>
      </div>
      <div class="aag-me-hero-overlay" data-panel-body="hero">
        ${brandLogoSvg ? `<div class="hotel-logo-container">${brandLogoSvg}</div>` : ""}
        ${title ? `<h3 class="aag-me-hero-title">${text(title)}</h3>` : ""}
        ${description ? `<p class="aag-me-hero-body">${text(description)}</p>` : ""}
      </div>
      ${panels}
    </div>

    <div class="at-a-glance-right">
      <div class="aag-me-images">${images}</div>
      ${
        stats || breakfastStat
          ? `<div class="hotel-capacity-container aag-me-stats">${stats}${breakfastStat}</div>`
          : ""
      }
      <div class="hotel-heading-container aag-me-place">
        <div class="location-pin-icon aag-me-pin">${HotelLocation}</div>
        ${country ? `<p class="hotel-country aag-me-country">${text(country)}</p>` : ""}
        ${city ? `<h3 class="hotel-city aag-me-city">${text(city)}</h3>` : ""}
        ${hotelName ? `<p class="hotel-name aag-me-hotel">${text(hotelName)}</p>` : ""}
        ${
          hotelUrl
            ? `<a class="aag-me-website" href="https://www.radissonhotels.com${esc(
                hotelUrl,
              )}" target="_blank" rel="noopener noreferrer"
                onclick="event.stopPropagation();">VISIT HOTEL WEBSITE</a>`
            : ""
        }
        ${
          hotelLocation
            ? `<p class="hotel-footer-location-container aag-me-location">
                 <span class="hotel-footer-location-label aag-me-location-label">Location:</span>
                 <span class="hotel-footer-location aag-me-location-value">${text(hotelLocation)}</span>
               </p>`
            : ""
        }
      </div>
      <nav class="aag-me-nav">${nav}</nav>
    </div>

    <div class="aag-fullscreen-viewer" onclick="aagCloseFullscreen(this.querySelector('.aag-fullscreen-close'));">
      <button class="aag-fullscreen-close" onclick="event.stopPropagation(); aagCloseFullscreen(this);">${closeBtnSvg}</button>
      <img class="aag-fullscreen-img" src="" alt="Full size image" onclick="event.stopPropagation();">
    </div>
    <button class="initial-back-btn" aria-label="Back to cover"
      onclick="event.stopPropagation(); aagInitialBack(this)">
      <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960"
           width="24px" fill="currentcolor">
        <path d="m560-240-56-58 142-142H160v-80h486L504-662l56-58 240 240-240 240Z"/>
      </svg>
    </button>
    ${galleryViewer(galleryGroupList, hotelName)}
    <button class="aag-expand-btn aag-me-expand-cols" aria-expanded="false"
      aria-label="Expand to full width"
      onclick="event.stopPropagation(); aagMeExpandColumns(this)">
      <svg xmlns="http://www.w3.org/2000/svg" width="8" height="16"
           viewBox="0 0 8 16" fill="none" stroke="currentcolor"
           stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m1.5 1.5 5 6.5-5 6.5"/></svg>
    </button>
  </div>`;
}
